import type { Instance as PopperInstance } from '@popperjs/core';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  List,
  ListItemButton,
  Popper,
  PopperPlacementType,
  Typography,
} from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { useSearch } from '@client/hooks/use-search.hook';
import { ValueField } from '@features/proposals/ElementListRow/ElementListRow';
import {
  getMenuListMaxHeightPx,
  MENU_ITEM_STYLE,
  MENU_STYLE,
} from '@ui/menus/Menu/menu.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  getTypeaheadDropdownWidthConstraints,
  resolveTypeaheadActiveIndex,
  resolveTypeaheadEnterAction,
} from './typeahead.helpers';

/**
 * Generic typeahead field. Composes the element list's {@link ValueField}
 * (same look + behaviour as the row's name input) with a dropdown list
 * of filtered options. Saves an item key, not the typed text.
 *
 * Shape:
 *   Parent <-[key]- <TypeaheadField>
 *                     <ValueField (text input / search)>
 *                     <Popper><List>...</List></Popper>
 *                   </TypeaheadField>
 *
 * Keyboard:
 *   - ArrowDown / ArrowUp move the active row (clamped).
 *   - Opening the list is unhighlighted. Hover wash starts only after the
 *     pointer moves inside the dropdown. Typing highlights the first match.
 *   - Enter picks a hovered/arrowed row or the first typed-search match.
 *     Empty field + Enter unsets. Bare open, or a typed query with no match,
 *     closes and keeps the prior pick.
 *   - Escape cancels editing and keeps the prior pick.
 *   - Tab / blur with an emptied field unsets; otherwise stop without picking.
 *
 * Focus:
 *   - On startEditing with no selection: empty search, full list.
 *   - On startEditing with a selection: search is prefilled with the current
 *     label (so reopen continues from that query).
 *   - When {@link TypeaheadFieldProps.isRowSelected} is `false` and a value is
 *     already chosen, the first plain click does not enter edit mode — it
 *     bubbles so the parent can select the row; a second click opens the
 *     dropdown. Shift/Cmd+click opens immediately (multi-select assign).
 *   - On stopEditing: focus returns to the wrapper Box (tabIndex=-1) so a
 *     subsequent Tab keeps moving instead of restarting from `<body>`.
 */
export type TypeaheadOption = { key: string; label: string };

const DROPDOWN_GAP_BELOW_PX = 2;
const DROPDOWN_GAP_ABOVE_PX = 4;
const VIEWPORT_EDGE_PADDING_PX = 8;
/** Theme spacing unit (see NodonTheme.spacing) — Menu uses the same. */
const THEME_SPACING_PX = 4;
const DROPDOWN_VERTICAL_PADDING_PX = MENU_STYLE.PADDING_Y * THEME_SPACING_PX;
const DROPDOWN_ITEM_PADDING_X_PX = MENU_ITEM_STYLE.PADDING_X * THEME_SPACING_PX;
/** Default visible rows before the dropdown scrolls (same as {@link Menu}). */
const DROPDOWN_MAX_HEIGHT_PX = getMenuListMaxHeightPx(THEME_SPACING_PX);
const DROPDOWN_ACTIVE_ROW_BG = 'rgba(0, 0, 0, 0.15)';
/** Extra wash on the select box above the row hover tint. */
const FIELD_HOVER_EXTRA_BG = 'rgba(0, 0, 0, 0.04)';
const FIELD_HOVER_TRANSITION = 'background-color 180ms ease';
/** Match {@link PillTabs} default tab radius. */
const PILL_BORDER_RADIUS = '5px';

const typeaheadLabelAlignSx = {
  maxWidth: '100%',
  alignSelf: 'center',
} as const;

const typeaheadEditingMinWidthSx = { minWidth: 0 } as const;

const typeaheadIdleHoverSx = {
  position: 'relative',
  width: 'fit-content',
  maxWidth: '100%',
  // Extra horizontal room beyond ValueField's 2px so the wash
  // reads a bit wider on each side of the label.
  px: '4px',
  borderRadius: PILL_BORDER_RADIUS,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    borderRadius: PILL_BORDER_RADIUS,
    pointerEvents: 'none',
    backgroundColor: 'transparent',
    transition: FIELD_HOVER_TRANSITION,
  },
  '&:hover::after': {
    backgroundColor: FIELD_HOVER_EXTRA_BG,
  },
} as const;

type TypedChangeHandler = (value: string) => void;

type DropdownViewportSpace = {
  above: number;
  below: number;
};

const getDropdownViewportSpace = (
  anchorEl: HTMLElement | null,
): DropdownViewportSpace => {
  if (!anchorEl) return { above: 0, below: 0 };
  const { top, bottom } = anchorEl.getBoundingClientRect();
  return {
    above: Math.max(0, top - DROPDOWN_GAP_ABOVE_PX - VIEWPORT_EDGE_PADDING_PX),
    below: Math.max(
      0,
      window.innerHeight -
        bottom -
        DROPDOWN_GAP_BELOW_PX -
        VIEWPORT_EDGE_PADDING_PX,
    ),
  };
};

/** Prefer below; open above when there is less room underneath than above. */
const resolveDropdownPlacement = (
  anchorEl: HTMLElement | null,
): PopperPlacementType => {
  const { above, below } = getDropdownViewportSpace(anchorEl);
  return below >= above ? 'bottom-start' : 'top-start';
};

const getDropdownMaxHeight = (
  anchorEl: HTMLElement | null,
  placement: PopperPlacementType,
): number => {
  const { above, below } = getDropdownViewportSpace(anchorEl);
  const viewportRoom = placement.startsWith('top') ? above : below;
  // Prefer ~12 visible rows; shrink further when the viewport is tighter.
  return Math.min(viewportRoom, DROPDOWN_MAX_HEIGHT_PX);
};

const getPopperModifiers = (placement: PopperPlacementType) => [
  {
    name: 'offset',
    options: {
      offset: [
        0,
        placement.startsWith('top')
          ? DROPDOWN_GAP_ABOVE_PX
          : DROPDOWN_GAP_BELOW_PX,
      ],
    },
  },
];

const getOverflowScrollParents = (el: HTMLElement | null): HTMLElement[] => {
  const parents: HTMLElement[] = [];
  let parent = el?.parentElement ?? null;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(`${overflow}${overflowY}`)) {
      parents.push(parent);
    }
    parent = parent.parentElement;
  }
  return parents;
};

export type TypeaheadFieldProps<T extends TypeaheadOption> = {
  items: readonly T[];
  /** Currently picked key; controls the display when not editing. */
  selectedKey: string | undefined;
  /** Shown in display mode when nothing is picked. */
  placeholder: string;
  /** Shown inside the input when focused/editing. Defaults to {@link placeholder}. */
  searchPlaceholder?: string;
  /**
   * Called with the chosen item's key, or `undefined` when the field is cleared
   * (Enter, or blur/Tab on an emptied field) to unset the current selection.
   */
  onPick: (key: string | undefined) => void;
  disabled?: boolean;
  /** Custom item content; defaults to `item.label` text. */
  renderItem?: (
    item: T,
    state: { active: boolean; selected: boolean },
  ) => React.ReactNode;
  /**
   * When `false` and {@link selectedKey} is set, click does not open the
   * dropdown (lets the parent select the row first). Omit or `true` to open
   * on the first click.
   */
  isRowSelected?: boolean;
  /**
   * Max items rendered in the dropdown (scroll within a ~12-row viewport).
   * Default 50.
   */
  maxItems?: number;
  /** Empty-state label shown when no items match the query. */
  emptyLabel?: string;
  /** Right padding on the field wrapper, e.g. to leave space before the column edge. */
  paddingRight?: number | string;
  /**
   * When true, the field hugs its label while idle and only grows to fill the
   * row while editing — for inline controls (e.g. an AI button) beside it.
   */
  shrinkWhenIdle?: boolean;
  /**
   * When nothing is picked, run before opening the dropdown. Return `true` to
   * consume the click (e.g. trigger AI mapping on first activate).
   */
  onEmptyActivate?: (ctx: { isRowSelected: boolean | undefined }) => boolean;
  /** Fires when the dropdown opens or closes. */
  onEditingChange?: (isEditing: boolean) => void;
};

export const TypeaheadField = <T extends TypeaheadOption>({
  items,
  selectedKey,
  placeholder,
  searchPlaceholder = placeholder,
  onPick,
  disabled,
  renderItem,
  isRowSelected,
  maxItems = 50,
  emptyLabel = 'No matches',
  paddingRight,
  shrinkWhenIdle = false,
  onEmptyActivate,
  onEditingChange,
}: TypeaheadFieldProps<T>): React.ReactElement => {
  const focusRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popperInstanceRef = useRef<PopperInstance | null>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [typed, setTyped] = useState('');
  // Highlight by item key so arrows/hover stick across list updates. A new
  // typed query clears the key so the first match is marked instead.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Prefill on open is not a search — first-match highlight waits for typing.
  const [userTypedSearch, setUserTypedSearch] = useState(false);
  // CSS hover stays off until the pointer moves inside the list.
  const [hoverArmed, setHoverArmed] = useState(false);
  // Escape must keep the prior pick; blur after clearing the field must unset.
  const escapePressedRef = useRef(false);
  // Empty query lists every item — Enter must not pick matches[0] unless the
  // user explicitly moved the highlight (arrows) or hovered a row.
  const highlightCommittedRef = useRef(false);
  // Blur can fire before React re-renders `typed` after the last keystroke;
  // keep a ref so clear-commit never reads a stale label.
  const typedRef = useRef(typed);
  typedRef.current = typed;

  const selected = useMemo(
    () => items.find((i) => i.key === selectedKey),
    [items, selectedKey],
  );

  // Fuzzy search via shared fuse.js hook (project default thresholds; key=label).
  const itemsArray = useMemo(() => [...items], [items]);
  const searched = useSearch(itemsArray, typed.trim(), { keys: ['label'] });
  const matches = useMemo(
    () => searched.slice(0, maxItems),
    [searched, maxItems],
  );
  const hasSearchQuery = userTypedSearch && typed.trim().length > 0;

  const active = useMemo(
    () => resolveTypeaheadActiveIndex(matches, activeKey, hasSearchQuery),
    [matches, activeKey, hasSearchQuery],
  );

  // Open as soon as the field is focused so empty-query browse works.
  const isDropdownOpen = isEditing;
  const [dropdownPlacement, setDropdownPlacement] =
    useState<PopperPlacementType>('bottom-start');
  const [layoutEpoch, setLayoutEpoch] = useState(0);

  const syncDropdownLayout = useCallback((): void => {
    setDropdownPlacement(resolveDropdownPlacement(anchorRef.current));
    setLayoutEpoch((epoch) => epoch + 1);
    popperInstanceRef.current?.update();
  }, []);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return;

    let rafId = 0;
    const scheduleSync = (): void => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncDropdownLayout);
    };

    scheduleSync();

    const scrollTargets = new Set<EventTarget>([
      window,
      document,
      ...getOverflowScrollParents(anchorRef.current),
    ]);
    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', scheduleSync, {
        passive: true,
        capture: true,
      });
    });
    window.addEventListener('resize', scheduleSync, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      scrollTargets.forEach((target) => {
        target.removeEventListener('scroll', scheduleSync, { capture: true });
      });
      window.removeEventListener('resize', scheduleSync);
    };
  }, [isDropdownOpen, syncDropdownLayout]);

  // Keep the active row visible while arrowing through long lists.
  useEffect(() => {
    if (!isDropdownOpen) return;
    const key = matches[active]?.key;
    if (!key) return;
    itemRefs.current[key]?.scrollIntoView({ block: 'nearest' });
  }, [active, matches, isDropdownOpen]);

  const startEditing = (e?: React.MouseEvent): void => {
    if (disabled) return;
    // First click on a filled field only selects the row — unless this click
    // already extends multi-select (Shift/Cmd), in which case open immediately
    // so one product/recipe can be applied to the whole selection.
    const multiSelectClick = !!e && (e.shiftKey || e.metaKey || e.ctrlKey);
    if (
      selectedKey === undefined &&
      !multiSelectClick &&
      onEmptyActivate?.({ isRowSelected })
    ) {
      return;
    }
    if (
      selectedKey !== undefined &&
      isRowSelected === false &&
      !multiSelectClick
    ) {
      return;
    }
    // Prefill with the current label so reopen continues from that query, but
    // do not treat it as a typed search — list stays unhighlighted until the
    // pointer moves or the user types / arrows.
    setTyped(selected?.label ?? '');
    setActiveKey(null);
    setUserTypedSearch(false);
    setHoverArmed(false);
    highlightCommittedRef.current = false;
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const stopEditing = (): void => {
    setIsEditing(false);
    onEditingChange?.(false);
    setTyped('');
    typedRef.current = '';
    setActiveKey(null);
    setUserTypedSearch(false);
    setHoverArmed(false);
    escapePressedRef.current = false;
    highlightCommittedRef.current = false;
    // Return focus to the wrapper so a subsequent Tab keeps moving forward
    // instead of resetting to <body>.
    requestAnimationFrame(() => {
      focusRef.current?.focus();
    });
  };

  const pick = (key: string): void => {
    onPick(key);
    stopEditing();
  };

  /**
   * Unset when the user emptied the field (backspace), unless they hit Escape.
   * @param fieldValue - Prefer the InlineTextField save payload when present;
   *   otherwise the latest typed ref (cancel/blur when prop and local already match).
   */
  const commitClearIfEmpty = (fieldValue = typedRef.current): void => {
    if (escapePressedRef.current) return;
    if (!fieldValue.trim() && selectedKey !== undefined) onPick(undefined);
  };

  // Blur / outside click reach here via ValueField.onSave when the input value
  // differs from the prop. Pass `v` so we unset even if `typed` state is one
  // frame behind the last backspace. When prop and local already match (both
  // empty), the cancel path ({@link handleStopEditing}) commits instead.
  const handleTypedChange = useCallback<TypedChangeHandler>((value) => {
    setTyped(value);
    setUserTypedSearch(true);
    // Drop leftover highlight (prior hover) so a search marks the first match;
    // an emptied field has no highlight and Enter unsets.
    setActiveKey(null);
    highlightCommittedRef.current = value.trim().length > 0;
  }, []);

  /** Mark a dropdown row after the pointer moves onto it (arms CSS hover too). */
  const highlightRow = useCallback((key: string): void => {
    highlightCommittedRef.current = true;
    setHoverArmed(true);
    setActiveKey(key);
  }, []);

  const onValueSave = (v: string): void => {
    commitClearIfEmpty(v);
    stopEditing();
  };

  // Escape and blur-after-clear both land here via ValueField.onStopEditing.
  const handleStopEditing = (): void => {
    commitClearIfEmpty();
    stopEditing();
  };

  const moveActive = (delta: number): void => {
    if (matches.length === 0) return;
    highlightCommittedRef.current = true;
    // Empty query has no highlight until the user arrows — first Down/Up
    // commits to the edge row instead of skipping past it.
    if (activeKey == null) {
      setActiveKey(
        (delta > 0 ? matches[0]?.key : matches[matches.length - 1]?.key) ??
          null,
      );
      return;
    }
    const next = Math.max(0, Math.min(matches.length - 1, active + delta));
    setActiveKey(matches[next]?.key ?? null);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      // Let InlineTextField cancel; skip unset in handleStopEditing.
      escapePressedRef.current = true;
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(+1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter') {
      // preventDefault stops InlineTextField's internal Enter swallow + the
      // trailing onKeyUp save path; we drive the pick ourselves.
      e.preventDefault();
      const item = active >= 0 ? matches[active] : undefined;
      const action = resolveTypeaheadEnterAction({
        activeItemKey: item?.key,
        highlightCommitted: highlightCommittedRef.current,
        userTypedSearch,
        queryEmpty: typedRef.current.trim().length === 0,
      });
      switch (action.type) {
        case 'pick':
          pick(action.key);
          return;
        case 'unset':
          if (selectedKey !== undefined) onPick(undefined);
          stopEditing();
          return;
        case 'close':
          stopEditing();
          return;
        default:
          assertUnreachable(action);
      }
    }
  };

  const displayLabel = selected?.label ?? '';

  return (
    <Box
      ref={focusRef}
      tabIndex={-1}
      sx={{
        flex: shrinkWhenIdle && !isEditing ? '0 1 auto' : 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        outline: 'none',
        paddingRight,
      }}
    >
      <Box
        ref={anchorRef}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Hover wash hugs the label (contentSized ValueField); expands only
            while editing so the input can fill the name column. */}
        <Box
          sx={mergeSx(
            typeaheadLabelAlignSx,
            { flex: isEditing ? 1 : '0 1 auto' },
            isEditing ? typeaheadEditingMinWidthSx : typeaheadIdleHoverSx,
          )}
        >
          <ValueField
            value={isEditing ? typed : displayLabel}
            isEditing={isEditing}
            onSave={onValueSave}
            onChange={handleTypedChange}
            onKeyDown={onKeyDown}
            onStartEditing={startEditing}
            onStopEditing={handleStopEditing}
            triggerOn="click"
            textAlign="left"
            // Row supplies the base hover wash; this field only adds a light
            // extra grey via the wrapper ::after above.
            showHover={false}
            fullWidth
            contentSized={!isEditing}
            placeholder={isEditing ? searchPlaceholder : placeholder}
            displayStyle={
              selected ? { color: 'rgba(0,0,0,0.8)' } : { opacity: 0.5 }
            }
          />
        </Box>
      </Box>
      <Popper
        open={isDropdownOpen && anchorRef.current !== null}
        anchorEl={anchorRef.current}
        placement={dropdownPlacement}
        popperRef={popperInstanceRef}
        popperOptions={{ strategy: 'fixed' }}
        modifiers={getPopperModifiers(dropdownPlacement)}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        {() => {
          void layoutEpoch;
          const anchorEl = anchorRef.current;
          const dropdownMaxHeight = getDropdownMaxHeight(
            anchorEl,
            dropdownPlacement,
          );
          const { minWidth, maxWidth } = getTypeaheadDropdownWidthConstraints(
            anchorEl?.offsetWidth ?? 240,
          );
          const dropdownList =
            matches.length === 0 ? (
              <Box
                sx={{
                  px: `${DROPDOWN_ITEM_PADDING_X_PX}px`,
                  py: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {emptyLabel}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {matches.map((item, idx) => {
                  const isActive = active >= 0 && idx === active;
                  const isSelected = item.key === selectedKey;
                  return (
                    <ListItemButton
                      key={item.key}
                      disableGutters
                      ref={(el) => {
                        itemRefs.current[item.key] = el;
                      }}
                      selected={isSelected}
                      onMouseEnter={() => {
                        if (!hoverArmed) return;
                        highlightRow(item.key);
                      }}
                      onMouseMove={() => {
                        highlightRow(item.key);
                      }}
                      // mousedown fires before the input's blur, so the pick
                      // resolves before edit mode exits via blur. preventDefault
                      // keeps focus on the input until pick() runs stopEditing.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(item.key);
                      }}
                      sx={{
                        px: `${DROPDOWN_ITEM_PADDING_X_PX}px`,
                        typography: 'body2',
                        bgcolor: isActive ? DROPDOWN_ACTIVE_ROW_BG : undefined,
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          bgcolor:
                            hoverArmed || isActive
                              ? DROPDOWN_ACTIVE_ROW_BG
                              : 'transparent',
                        },
                        '&.Mui-selected': {
                          bgcolor: isActive
                            ? DROPDOWN_ACTIVE_ROW_BG
                            : undefined,
                          '&:hover': {
                            bgcolor:
                              hoverArmed || isActive
                                ? DROPDOWN_ACTIVE_ROW_BG
                                : 'transparent',
                          },
                        },
                      }}
                    >
                      {renderItem?.(item, {
                        active: isActive,
                        selected: isSelected,
                      }) ?? (
                        <Typography variant="body2" noWrap>
                          {item.label}
                        </Typography>
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            );
          const dropdownScrollMaxHeight = Math.max(
            0,
            dropdownMaxHeight - DROPDOWN_VERTICAL_PADDING_PX * 2,
          );
          return (
            <Box
              sx={{
                // Match element-list {@link Menu} paper chrome.
                // Width is locked to the field so long labels ellipsis.
                width: minWidth,
                minWidth,
                maxWidth,
                maxHeight: dropdownMaxHeight,
                overflow: 'hidden',
                py: `${DROPDOWN_VERTICAL_PADDING_PX}px`,
                typography: 'body2',
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0, 0, 0, 0.3)',
                borderRadius: PILL_BORDER_RADIUS,
                boxShadow: '0px 1px 20px 0px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Box
                onMouseMove={() => {
                  setHoverArmed(true);
                }}
                sx={{
                  maxHeight: dropdownScrollMaxHeight,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                }}
              >
                {dropdownList}
              </Box>
            </Box>
          );
        }}
      </Popper>
    </Box>
  );
};
